import type { ReactElement } from 'react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router'

import { Button } from '#design-system/component/Button/Button'
import { Input } from '#design-system/component/Form/Input/Input'
import { Banner } from '#design-system/component/Layout/Banner/Banner'
import { getAuthRoute } from '~/common/utils/auth-routes'

import { AuthLayout } from './AuthLayout'

export const ResetPassword = (): ReactElement => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  // Reset token comes in as oobCode parameter from email link
  const token = searchParams.get('oobCode') ?? ''

  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Show error if no token provided
  if (!token) {
    return (
      <AuthLayout title={t('auth.reset_password')} type="reset-password">
        <Banner
          bodyText={t('auth.invalid_reset_link_body')}
          headerText={t('auth.invalid_reset_link')}
          semanticType="Error"
        />
        <Button
          actionRole="secondary"
          label={t('auth.request_new_link')}
          onClick={() => navigate('/auth/forgot-password')}
        />
      </AuthLayout>
    )
  }

  const handleSubmit = async (): Promise<void> => {
    setError(null)
    setIsSubmitting(true)

    try {
      const response = await fetch(getAuthRoute('confirm-reset-password'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ token, newPassword: password }),
      })

      if (!response.ok) {
        const data = (await response.json().catch(() => ({}))) as { message?: string }
        throw new Error(data.message ?? t('auth.reset_password_failed'))
      }

      // Success - redirect to login with success message
      void navigate('/auth/login?resetSuccess=true', { replace: true })
    } catch (err) {
      const message = err instanceof Error ? err.message : t('auth.reset_password_failed')
      setError(message)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <AuthLayout
      subtitle={t('auth.reset_password_subtitle')}
      title={t('auth.reset_password')}
      type="reset-password"
    >
      {error && <Banner bodyText={error} headerText={t('auth.error')} semanticType="Error" />}

      <form
        onSubmit={(e) => {
          e.preventDefault()
          void handleSubmit()
        }}
      >
        <Input
          autoComplete="new-password"
          helperText={t('auth.password_requirements')}
          label={t('auth.new_password')}
          minLength={8}
          onChange={setPassword}
          onLiveChange={setPassword}
          required
          size="full"
          type="password"
          value={password}
        />

        <Button
          actionRole="primary"
          disabledMessage={isSubmitting ? t('auth.resetting') : undefined}
          label={t('auth.reset_password')}
          type="submit"
        />
      </form>
    </AuthLayout>
  )
}
